import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import {
  BookOpen,
  Pencil,
  X,
  Check,
  Upload,
  Trash2,
  File,
  Loader,
} from "lucide-react";
import { uploadDocumentFile } from "../../apis/documents";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

// ─── Collapsed View ───────────────────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const content = data.content || "";
  const attachments = data.attachments || [];

  return (
    <div className="min-w-[200px] max-w-[260px]">
      {/* Title bar */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <BookOpen size={16} className="text-amber-600" />
          <span className="font-bold text-sm text-amber-900">Knowledge Base</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="p-1 hover:bg-amber-200 rounded-md transition-colors"
          title="Edit knowledge base"
        >
          <Pencil size={14} className="text-amber-700" />
        </button>
      </div>

      {/* Content preview */}
      {content ? (
        <div className="text-xs text-amber-800 mb-1.5 line-clamp-3">
          {content.substring(0, 100)}
          {content.length > 100 ? "..." : ""}
        </div>
      ) : (
        <div className="text-xs text-amber-500 italic mb-1.5">No content added</div>
      )}

      {/* Attachments count */}
      {attachments.length > 0 && (
        <div className="flex items-center gap-1.5">
          <File size={11} className="text-amber-500" />
          <span className="text-[10px] text-amber-600 font-semibold">
            {attachments.length} file{attachments.length > 1 ? "s" : ""} attached
          </span>
        </div>
      )}

      {/* Attached-to indicator */}
      {data.attachedToNodeId && (
        <div className="flex items-center gap-1 mt-1.5 px-1.5 py-0.5 bg-green-100 rounded">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
          <span className="text-[9px] text-green-700 font-semibold">Attached to node</span>
        </div>
      )}
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel }) => {
  const [content, setContent] = useState(data.content || "");
  const [attachments, setAttachments] = useState(
    data.attachments ? [...data.attachments] : []
  );
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError("");
    setUploading(true);

    try {
      const response = await uploadDocumentFile(file);
      const fileUrl = response.data.data.doc_url;
      setAttachments((prev) => [
        ...prev,
        {
          fileName: file.name,
          fileUrl,
          fileType: file.name.split(".").pop() || "unknown",
        },
      ]);
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onSave({ content, attachments });
  };

  return (
    <div
      className="min-w-[300px] max-w-[360px] space-y-3 nowheel"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-amber-900">Edit Knowledge Base</span>
        <div className="flex gap-1">
          <button onClick={handleSave} className="p-1 bg-green-100 hover:bg-green-200 rounded transition-colors" title="Save">
            <Check size={14} className="text-green-700" />
          </button>
          <button onClick={onCancel} className="p-1 bg-red-100 hover:bg-red-200 rounded transition-colors" title="Cancel">
            <X size={14} className="text-red-700" />
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="knowledgeBase">
      {/* Content textarea */}
      <div className="space-y-1">
        <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Content</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Enter knowledge base content... This content will be used by AI to answer user questions."
          rows={6}
          className="w-full px-2.5 py-2 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-amber-400 focus:outline-none resize-none leading-relaxed"
        />
      </div>

      {/* File attachments */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Attachments</label>

        {/* Uploaded files list */}
        {attachments.length > 0 && (
          <div className="space-y-1">
            {attachments.map((att, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <File size={13} className="text-amber-500 flex-shrink-0" />
                  <span className="text-[11px] text-gray-700 font-medium truncate">
                    {att.fileName}
                  </span>
                  <span className="text-[9px] text-gray-400 uppercase flex-shrink-0">
                    {att.fileType}
                  </span>
                </div>
                <button
                  onClick={() => removeAttachment(index)}
                  className="p-0.5 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        <label className="block cursor-pointer">
          <div
            className={`border-2 border-dashed rounded-lg p-2.5 text-center transition-colors ${
              uploading
                ? "border-amber-300 bg-amber-50"
                : "border-gray-300 hover:border-amber-400 hover:bg-amber-50"
            }`}
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2">
                <Loader size={14} className="text-amber-500 animate-spin" />
                <span className="text-[11px] text-amber-600 font-semibold">Uploading...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Upload size={14} className="text-gray-400" />
                <span className="text-[11px] font-semibold text-gray-600">Upload File</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.json"
            onChange={handleFileUpload}
            disabled={uploading}
          />
        </label>

        {uploadError && (
          <p className="text-[10px] text-red-600 font-medium">{uploadError}</p>
        )}
      </div>
      </NodeEditorTabs>

      {/* Bottom actions */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSave}
          className="flex-1 py-1.5 text-xs font-bold bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors flex items-center justify-center gap-1"
        >
          <Check size={12} />
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-1.5 text-xs font-bold bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors border border-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main KnowledgeBaseNode ───────────────────────────────────────
const KnowledgeBaseNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("knowledgeBase");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, {
          ...data,
          content: formData.content,
          attachments: formData.attachments,
        });
      }
      closeEditor();
    },
    [id, data]
  );

  const handleCancel = useCallback(() => {
    closeEditor();
  }, []);

  return (
    <div className="relative bg-amber-50 border-2 border-amber-400 rounded-xl p-3 shadow-sm shadow-md transition-all">
      <NodeActionButtons id={id} data={data} />
      {/* Knowledge Base nodes have no incoming connections — only outgoing to AI KB Answer / Template */}

      {/* Content */}
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor data={data} onSave={handleSave} onCancel={handleCancel} presentation="fullscreen" />
      </FlowNodeEditorShell>

      {/* Source handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="default"
        className="!w-4 !h-4 !bg-amber-500 !border-2 !border-white"
        style={{ right: -8 }}
      />
    </div>
  );
};

export default KnowledgeBaseNode;
